import { Button, ButtonBase, IconButton, MenuItem, ToggleButton, useMediaQuery } from '@mui/material'
import { styled, Theme } from '@mui/system'

export const StyledButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  backgroundColor: theme.palette.grey[300],
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}))

export const StyledButtonPrimary = styled(Button)(({ theme }) => ({
  height: theme.spacing(4),
  borderRadius: theme.spacing(1),
  minWidth: 'max-content',
  whiteSpace: 'nowrap',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
}))

export const StyledButtonSecondary = styled(Button)(({ theme }) => ({
  whiteSpace: 'nowrap',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.grey[900],
  height: theme.spacing(4),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.grey[300],
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}))

export const StyledButtonTechTile = styled(ButtonBase)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
  borderRadius: theme.spacing(1),
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  gap: theme.spacing(2),
  color: theme.palette.text.light,
  width: '100%',
  height: '70px',
  minWidth: '20px',
  textTransform: 'none',
  transition: 'all 150ms ease-in-out',
  ':hover': {
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 4px 12px',
    backgroundColor: theme.palette.background.elevation2,
  },
  ':focus': {
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 4px 12px',
    backgroundColor: theme.palette.background.elevation2,
  },
}))

export const StyledButtonMenuItem = styled(Button)(({ theme }) => ({
  width: '100%',
  paddingLeft: theme.spacing(2.5),
  height: theme.spacing(4),
  borderRadius: theme.spacing(0),
  gap: theme.spacing(0.5),
  minWidth: 'max-content',
  whiteSpace: 'nowrap',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  ':focus': {
    backgroundColor: theme.palette.background.elevation2,
  },
}))

export const StyledMenuItemAsButton = styled(MenuItem)(({ theme }) => ({
  width: '100%',
  paddingLeft: theme.spacing(2.5),
  height: theme.spacing(4),
  borderRadius: theme.spacing(0),
  gap: theme.spacing(0.5),
  minWidth: 'max-content',
  whiteSpace: 'nowrap',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  ':focus': {
    backgroundColor: theme.palette.background.elevation2,
  },
}))

export const StyledButtonGrayed = styled(StyledButtonPrimary)(({ theme }) => ({
  backgroundColor: theme.palette.grey[300],
}))

export const StyledIconButtonSchedule = styled(IconButton)(({ theme }) => {
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')) // Move this inside a functional component
  return {
    width: 32,
    height: 32,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: 'black',
    svg: {
      width: isSmallScreen ? 20 : 24,
      height: isSmallScreen ? 20 : 24,
    },
  }
})

// Figma design system buttons
const ColouredIconButton = styled(IconButton)(({ theme }) => {
  return {
    '&.MuiIconButton-colorPrimary': primaryStyles(theme),
    '&.MuiIconButton-colorSecondary': secondaryStyles(theme),
  }
})

const primaryStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.lightContrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.lightHover,
  },
  '&:disabled': disabledStyles(theme),
})

const secondaryStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.mainContrastText,
  '&:hover': {
    backgroundColor: theme.palette.secondary.mainHover,
  },
  '&:disabled': disabledStyles(theme),
})

const activeStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.hover,
  },
})

const disabledStyles = (theme: Theme) => ({
  '&:disabled': {
    backgroundColor: theme.palette.disabled.main,
    color: theme.palette.disabled.mainContrastText,
    boxShadow: 'none',
  },
})

export const SquareToggleButton = styled(ToggleButton)(({ theme }) => {
  return {
    border: 'none',
    transition: 'all 150ms ease-in-out',
    '&.MuiToggleButton-primary': primaryStyles(theme),
    '&.MuiToggleButton-secondary': secondaryStyles(theme),
    '&.Mui-selected': activeStyles(theme),
    '&:disabled': {
      border: 'none',
    },
  }
})

export const BasicToggleButton = styled(ToggleButton)(({ theme }) => {
  return {
    height: '36px',
    border: 'none',
    transition: 'all 150ms ease-in-out',
    '&.MuiToggleButton-primary': primaryStyles(theme),
    '&.MuiToggleButton-secondary': secondaryStyles(theme),
    '&.Mui-selected': activeStyles(theme),
  }
})

export const SquareButton = styled(ColouredIconButton, {
  shouldForwardProp: prop => prop !== 'component',
})(({ theme }) => {
  return {
    '&.MuiIconButton-sizeSmall': {
      width: 30,
      height: 30,
      '& svg': {
        height: '18px',
        width: '18px',
      },
    },
    '&.MuiIconButton-sizeMedium': {
      width: 36,
      height: 36,
      '& svg': {
        height: '20px',
        width: '20px',
      },
    },
    '&.MuiIconButton-sizeLarge': {
      width: 42,
      height: 42,
      '& svg': {
        height: '22px',
        width: '22px',
      },
    },
    borderRadius: '8px',
    backgroundColor: theme.palette.primary.main,
    color: 'black',
    svg: {},
  }
})

export const ExtraSmallSquareButton = styled(ColouredIconButton)(({ theme }) => {
  return {
    width: '24px',
    height: '24px',
    '& svg': {
      height: '18px',
      width: '18px',
    },
    borderRadius: '8px',
  }
})

export const RoundButton = styled(SquareButton)(({ theme }) => {
  return {
    borderRadius: '100%',
  }
})